import React, {
	forwardRef,
	FC,
	ReactNode,
	useLayoutEffect,
	useRef,
	ReactElement,
	HTMLAttributes,
	useCallback,
	useEffect,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Portal from '../../layout/Portal/Portal';
import TagWrapper from '../TagWrapper';
import useEventListener from '../../hooks/useEventListener';
import {
	Modal as RBSModal,
	ModalProps as RBSModalProps,
	ModalBody as RBSModalBody,
	ModalHeader as RBSModalHeader,
	ModalTitle as RBSModalTitle,
	ModalFooter as RBSModalFooter,
} from 'react-bootstrap'; 

interface IModalTitleProps extends HTMLAttributes<HTMLElement> {
	id: string;
	children: ReactNode;
	className?: string;
	tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
}

export const ModalTitle = forwardRef<HTMLHeadingElement, IModalTitleProps>(
	({ tag, id, children, className, ...props }, ref) => {
		return (
			<RBSModalTitle 
				as={TagWrapper}
				tag={tag}
				ref={ref}
				id={id}
				className={classNames('modal-title', className)}
				{...props}
			>
				{children}
			</RBSModalTitle>
		);
	},
);

ModalTitle.displayName = 'ModalTitle';
ModalTitle.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	tag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span']),
};
ModalTitle.defaultProps = {
	className: undefined,
	tag: 'h5',
};

interface IModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactElement<IModalTitleProps> | ReactNode;
	className?: string;
	setIsOpen?(...args: unknown[]): unknown | undefined;
}

export const ModalHeader = forwardRef<HTMLDivElement, IModalHeaderProps>(
	({ children, className, setIsOpen, ...props }, ref) => {
		return (
			<RBSModalHeader 
				ref={ref} 
				className={classNames('modal-header', className)} 
				{...props}
			>
				{children}
				{setIsOpen && (
					<button
						type='button'
						className='btn-close'
						data-bs-dismiss='modal'
						aria-label='Close'
						onClick={() => setIsOpen(false)}
					/>
				)}
			</RBSModalHeader>
		);
	},
);

ModalHeader.displayName = 'ModalHeader';
ModalHeader.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	setIsOpen: PropTypes.func,
};
ModalHeader.defaultProps = {
	className: undefined,
	setIsOpen: undefined,
};

interface IModalBodyProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}

export const ModalBody = forwardRef<HTMLDivElement, IModalBodyProps>(
	({ children, className, ...props }, ref) => {
		return (
			<RBSModalBody 
				ref={ref} 
				className={classNames('modal-body', className)} 
				{...props}
			>
				{children}
			</RBSModalBody>
		);
	},
);

ModalBody.displayName = 'ModalBody';
ModalBody.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
ModalBody.defaultProps = {
	className: undefined,
};

interface IModalFooterProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	className?: string;
}

export const ModalFooter = forwardRef<HTMLDivElement, IModalFooterProps>(
	({ children, className, ...props }, ref) => {
		return (
			<RBSModalFooter 
				ref={ref} 
				className={classNames('modal-footer', className)} 
				{...props}
			>
				{children}
			</RBSModalFooter>
		);
	},
);

ModalFooter.displayName = 'ModalFooter';
ModalFooter.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
ModalFooter.defaultProps = {
	className: undefined,
};

interface IModalProps extends Omit<RBSModalProps, 'children' | 'show' | 'onHide' | 'backdrop'> {
	children: ReactNode;
	isOpen: boolean;
	setIsOpen(...args: unknown[]): unknown;
	id?: string | undefined;
	titleId?: string;
	isStaticBackdrop?: boolean;
	isScrollable?: boolean;
	isCentered?: boolean;
	size?: 'sm' | 'md' | 'lg' | 'xl' | null;
	fullScreen?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | boolean;
	isAnimation?: boolean;
	classNameMd?: string;
}

const Modal: FC<IModalProps> = ({
	children,
	isOpen,
	setIsOpen,
	id,
	titleId,
	isStaticBackdrop,
	isScrollable,
	isCentered,
	size,
	fullScreen,
	isAnimation,
	classNameMd,
	className,
	backdropClassName,
	dialogClassName,
	contentClassName,
	animation,
	...props
}) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const [modalElement, setModalElement] = useState<HTMLElement | null>(null);

	// <body> modal-open class (presentation)
	useLayoutEffect(() => {
		if (isOpen) {
			document.body.classList.add('modal-open');
		}
		return () => {
			document.body.classList.remove('modal-open');
		};
	}, [isOpen]);

	// Find the modal DOM element after it's rendered
	useEffect(() => {
		if (isOpen) {
			// Try to find the modal element in the DOM
			const timer = setTimeout(() => {
				const modalEl = document.querySelector('.modal.show');
				if (modalEl) {
					setModalElement(modalEl as HTMLElement);
				}
			}, 100);

			return () => clearTimeout(timer);
		}
		return undefined;
	}, [isOpen]);

	// Keypress close function
	const escFunction = useCallback((event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			setIsOpen(false);
		}
	}, [setIsOpen]);

	useEventListener('keydown', escFunction);

	const ANIMATION_PROPS = isAnimation
		? {
				initial: { opacity: 0, y: '-50%' },
				animate: { opacity: 1, x: '0%', y: '0%' },
				exit: { opacity: 0, y: '-50%' },
				transition: { ease: 'easeInOut', duration: 0.3 },
		  }
		: null;

	// Map fullScreen prop to react-bootstrap format
	const getFullscreenValue = (): true | string | undefined => {
		if (!fullScreen) return undefined;
		if (fullScreen === true) return true;
		if (typeof fullScreen === 'string') {
			return `${fullScreen}-down`;
		}
		return undefined;
	};

	// Convert size 'md' to undefined (react-bootstrap doesn't have 'md')
	const getSize = () => {
		if (size === 'md' || size === null) return undefined;
		return size;
	};

	// Handle static backdrop - using event delegation
	const handleModalClick = useCallback((event: React.MouseEvent) => {
		if (!isStaticBackdrop || !modalElement) return;
		
		// Check if click is on the backdrop (modal element itself, not its children)
		if (event.target === modalElement) {
			modalElement.classList.add('modal-static');
			setTimeout(() => {
				modalElement.classList.remove('modal-static');
			}, 300);
		}
	}, [isStaticBackdrop, modalElement]);

	// Alternative approach: use event listener on the backdrop
	useEffect(() => {
		if (!isStaticBackdrop || !modalElement) return;

		const handleBackdropClick = (event: MouseEvent) => {
			// Check if click is on the backdrop
			const backdrop = modalElement.querySelector('.modal-backdrop');
			if (backdrop && event.target === backdrop) {
				modalElement.classList.add('modal-static');
				setTimeout(() => {
					modalElement.classList.remove('modal-static');
				}, 300);
			}
		};

		modalElement.addEventListener('click', handleBackdropClick);
		return () => {
			modalElement.removeEventListener('click', handleBackdropClick);
		};
	}, [isStaticBackdrop, modalElement]);

	return (
		<Portal>
			<AnimatePresence exitBeforeEnter>
				{isOpen && (
					<motion.div
						key='modal-wrapper'
						{...ANIMATION_PROPS}
					>
						<RBSModal
							ref={modalRef}
							show={isOpen}
							onHide={() => setIsOpen(false)}
							backdrop={isStaticBackdrop ? 'static' : true}
							keyboard={!isStaticBackdrop}
							className={classNames(classNameMd, className, { fade: isAnimation || animation })}
							backdropClassName={classNames('modal-backdrop', classNameMd, { fade: isAnimation || animation }, backdropClassName)}
							dialogClassName={classNames(dialogClassName, {
								'modal-dialog-scrollable': isScrollable,
								'modal-dialog-centered': isCentered,
								[`modal-${size}`]: size && size !== 'md',
								[`modal-fullscreen${typeof fullScreen === 'string' ? `-${fullScreen}-down` : ''}`]: fullScreen,
								'modal-size': true,
							})}
							contentClassName={contentClassName}
							size={getSize()}
							fullscreen={getFullscreenValue()}
							scrollable={isScrollable}
							centered={isCentered}
							id={id}
							aria-labelledby={titleId}
							animation={isAnimation || animation}
							{...props}
						>
							{children}
						</RBSModal>
					</motion.div>
				)}
			</AnimatePresence>
		</Portal>
	);
};

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	children: PropTypes.node.isRequired,
	id: PropTypes.string,
	titleId: PropTypes.string,
	isStaticBackdrop: PropTypes.bool,
	isScrollable: PropTypes.bool,
	isCentered: PropTypes.bool,
	size: PropTypes.oneOf([null, 'sm', 'lg', 'xl', 'md']),
	fullScreen: (PropTypes.oneOfType([
		PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl']),
		PropTypes.bool,
	]) as PropTypes.Requireable<boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'>),
	isAnimation: PropTypes.bool,
	classNameMd: PropTypes.string,
	className: PropTypes.string,
	backdropClassName: PropTypes.string,
	dialogClassName: PropTypes.string,
	contentClassName: PropTypes.string,
	animation: PropTypes.bool,
};

Modal.defaultProps = {
	id: undefined,
	isStaticBackdrop: false,
	isScrollable: false,
	isCentered: false,
	size: null,
	fullScreen: false,
	isAnimation: true,
	titleId: undefined,
	classNameMd: undefined,
	className: undefined,
	backdropClassName: undefined,
	dialogClassName: undefined,
	contentClassName: undefined,
	animation: undefined,
};

// Attach subcomponents to Modal for easier access
const ModalWithSubcomponents = Object.assign(Modal, {
	Title: ModalTitle,
	Header: ModalHeader,
	Body: ModalBody,
	Footer: ModalFooter,
});

export default ModalWithSubcomponents;